import { Form, Formik } from 'formik'
import { useState } from 'react'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IChecklist } from '~/types/checklist'
import type { IUserPublic } from '~/types/user'
import type { IAppVersionSummary } from '~/types/version'

import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/Formik/DateInput'
import MultiSelectInput from '~/components/Inputs/Formik/MultiSelectInput'
import SelectInput from '~/components/Inputs/Formik/SelectInput'
import TextArea from '~/components/Inputs/Formik/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { CHECKLIST_TEMPLATE_LABEL_MAPPING, FORM_ERRORS } from '~/const'
import {
  useCreateOrUpdateMwmAppChecklistMutation,
  useCreateOrUpdateReleaseChecklistMutation,
  useCreatePublishingAppChecklistMutation,
  useUpdatePublishingAppChecklistMutation,
} from '~/queries/checklists'
import { useUsersListQuery } from '~/queries/users'
import { useVersionsDetailsQuery, useVersionsListQuery } from '~/queries/versions'
import { CHECKLIST_TEMPLATE } from '~/types/checklist'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { capitalize } from '~/utils/string'
import { YupOption } from '~/utils/yup'

const MODAL_ID = 'legacy-checklist-modal'

export interface Values {
  adminTeam: IOption[]
  backendTeam: IOption[]
  businessDevelopmentTeam: IOption[]
  changelog: string
  dataTeam: IOption[]
  developersTeam: IOption[]
  marketingTeam: IOption[]
  monetizationTeam: IOption[]
  productManagerTeam: IOption[]
  productMonetizationSquadTeam: IOption[]
  productOwnerTeam: IOption[]
  relatedVersion: IOption | null
  releaseDate: string
  template: IOption
  uaTeam: IOption[]
}

const arrayValidator = Yup.array().of(YupOption).min(1, FORM_ERRORS.AT_LEAST_ONE)

const releaseTemplateValidationSchema = Yup.object().shape({
  changelog: Yup.string().required(FORM_ERRORS.REQUIRED),
  developersTeam: arrayValidator,
  productManagerTeam: arrayValidator,
  productMonetizationSquadTeam: arrayValidator,
  relatedVersion: YupOption.nullable().required(FORM_ERRORS.REQUIRED),
  releaseDate: Yup.string().required(FORM_ERRORS.REQUIRED),
})

const publishingAppValidationSchema = Yup.object().shape({
  backendTeam: arrayValidator,
  businessDevelopmentTeam: arrayValidator,
  mobileTeam: arrayValidator,
  productManagerTeam: arrayValidator,
  uaTeam: arrayValidator,
})

const mwmAppValidationSchema = Yup.object().shape({
  adminTeam: arrayValidator,
  backendTeam: arrayValidator,
  dataTeam: arrayValidator,
  developersTeam: arrayValidator,
  marketingTeam: arrayValidator,
  monetizationTeam: arrayValidator,
  productOwnerTeam: arrayValidator,
  uaTeam: arrayValidator,
})

interface FormProps {
  appId: string
  checklist?: IChecklist | null
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  version?: IAppVersionSummary
}

const ChecklistForm = ({
  appId,
  checklist,
  onClose,
  onFailure,
  onSuccess,
  version,
}: FormProps): ReactElement | null => {
  const defaultTemplate = checklist?.template ? checklist.template : CHECKLIST_TEMPLATE.VERSION_RELEASE

  const checklistValidation = (checklistTemplate: CHECKLIST_TEMPLATE) => {
    switch (checklistTemplate) {
      case CHECKLIST_TEMPLATE.VERSION_RELEASE:
        return releaseTemplateValidationSchema
      case CHECKLIST_TEMPLATE.NEW_PUBLISHING_APP:
        return publishingAppValidationSchema
      case CHECKLIST_TEMPLATE.NEW_MWM_APP:
        return mwmAppValidationSchema
      default:
        return undefined
    }
  }

  const [validationSchema, setValidationSchema] = useState(checklistValidation(defaultTemplate))

  const { data: users, isLoading: usersLoading } = useUsersListQuery()

  const { data: appVersions } = useVersionsListQuery(
    { appId },
    { enabled: !version && checklist?.template !== CHECKLIST_TEMPLATE.NEW_PUBLISHING_APP }
  )

  const { data: versionPerId, isLoading: versionPerIdLoading } = useVersionsDetailsQuery(
    { appId, versionId: checklist?.application_version_id },
    { enabled: !!checklist?.application_version_id }
  )

  const { mutateAsync: createOrUpdateReleaseChecklistMutation } = useCreateOrUpdateReleaseChecklistMutation()

  const { mutateAsync: createPublishingAppChecklistMutation } = useCreatePublishingAppChecklistMutation()

  const { mutateAsync: updatePublishingAppChecklistMutation } = useUpdatePublishingAppChecklistMutation()

  const { mutateAsync: createOrUpdateMwmAppChecklistMutation } = useCreateOrUpdateMwmAppChecklistMutation()

  const findUser = (id: string) => users?.find(({ user_id }) => user_id === id)

  const removeUndefined = (user?: IUserPublic): user is IUserPublic => !!user

  const mapUserToIDropdownValue = ({ name, user_id }: IUserPublic) => ({
    label: capitalize(name),
    value: user_id,
  })

  const getUserInitialValues = (ids: string[]): IOption[] =>
    ids.map(findUser).filter(removeUndefined).map(mapUserToIDropdownValue)

  if ((!!checklist?.application_version_id && versionPerIdLoading) || usersLoading) {
    return null
  }

  const getRelatedVersion = (): IOption | null => {
    if (checklist) {
      return { label: checklist.application_semantic_version || '', value: checklist.application_version_id || '' }
    }
    if (version) {
      return { label: version.semantic_version, value: version.version_id }
    }
    return null
  }

  const handleSubmit = async (values: Values) => {
    const {
      adminTeam,
      backendTeam,
      businessDevelopmentTeam,
      changelog,
      dataTeam,
      developersTeam,
      marketingTeam,
      monetizationTeam,
      productManagerTeam,
      productMonetizationSquadTeam,
      productOwnerTeam,
      relatedVersion,
      releaseDate,
      template: templateOption,
      uaTeam,
    } = values

    const adminTeamToAPI = adminTeam.map(({ value }) => value)
    const backendTeamToAPI = backendTeam.map(({ value }) => value)
    const businessDevelopmentTeamToAPI = businessDevelopmentTeam.map(({ value }) => value)
    const dataTeamToAPI = dataTeam.map(({ value }) => value)
    const developersTeamToAPI = developersTeam.map(({ value }) => value)
    const marketingTeamToAPI = marketingTeam.map(({ value }) => value)
    const monetizationTeamToAPI = monetizationTeam.map(({ value }) => value)
    const productManagerTeamToAPI = productManagerTeam.map(({ value }) => value)
    const productMonetizationSquadTeamToAPI = productMonetizationSquadTeam.map(({ value }) => value)
    const productOwnerTeamToAPI = productOwnerTeam.map(({ value }) => value)
    const uaTeamToAPI = uaTeam.map(({ value }) => value)
    const relatedVersionToAPI = relatedVersion?.value as string
    const template = templateOption.value

    switch (template) {
      case CHECKLIST_TEMPLATE.VERSION_RELEASE:
        try {
          await createOrUpdateReleaseChecklistMutation({
            changelog,
            developersTeamToAPI,
            productManagerTeamToAPI,
            productMonetizationSquadTeamToAPI,
            relatedVersionToAPI,
            releaseDate,
          })
          onSuccess()
        } catch {
          onFailure()
        }
        break
      case CHECKLIST_TEMPLATE.NEW_PUBLISHING_APP:
        try {
          if (checklist) {
            await updatePublishingAppChecklistMutation({
              appId,
              backendTeamToAPI,
              businessDevelopmentTeamToAPI,
              checklistId: checklist.id,
              developersTeamToAPI,
              productManagerTeamToAPI,
              uaTeamToAPI,
            })
            onSuccess()
          } else {
            await createPublishingAppChecklistMutation({
              appId,
              backendTeamToAPI,
              businessDevelopmentTeamToAPI,
              developersTeamToAPI,
              productManagerTeamToAPI,
              uaTeamToAPI,
            })
            onSuccess()
          }
        } catch {
          onFailure()
        }
        break
      case CHECKLIST_TEMPLATE.NEW_MWM_APP:
        try {
          await createOrUpdateMwmAppChecklistMutation({
            adminTeamToAPI,
            appId,
            backendTeamToAPI,
            checklistId: checklist?.id,
            dataTeamToAPI,
            developersTeamToAPI,
            marketingTeamToAPI,
            monetizationTeamToAPI,
            productOwnerTeamToAPI,
            uaTeamToAPI,
          })

          onSuccess()
        } catch {
          onFailure()
        }
    }
  }

  const relatedVersionOptions =
    appVersions
      ?.filter(({ release_version_checklist_id }) => release_version_checklist_id === '')
      .map(({ id, semantic_version }) => ({ label: semantic_version, value: id })) || []

  const userOptions =
    users?.map(({ name, picture, profile, user_id }) => {
      return {
        label: capitalize(name),
        userData: {
          job: profile?.job_title,
          name: capitalize(name),
          picture: picture,
        },
        value: user_id,
      }
    }) || []

  const initialValues: Values = {
    adminTeam: getUserInitialValues(checklist?.assignation_mapping['admin'] || []),
    backendTeam: getUserInitialValues(checklist?.assignation_mapping['backend'] || []),
    businessDevelopmentTeam: getUserInitialValues(checklist?.assignation_mapping['business_development'] || []),
    changelog: versionPerId?.changelog || '',
    dataTeam: getUserInitialValues(checklist?.assignation_mapping['data_team'] || []),
    developersTeam: getUserInitialValues(checklist?.assignation_mapping['developers_team'] || []),
    marketingTeam: getUserInitialValues(checklist?.assignation_mapping['marketing_team'] || []),
    monetizationTeam: getUserInitialValues(checklist?.assignation_mapping['monetization_team'] || []),
    productManagerTeam: getUserInitialValues(checklist?.assignation_mapping['product_managers_team'] || []),
    productMonetizationSquadTeam: getUserInitialValues(
      checklist?.assignation_mapping['product_monetization_squad_team'] || []
    ),
    productOwnerTeam: getUserInitialValues(checklist?.assignation_mapping['product_owners'] || []),
    relatedVersion: getRelatedVersion(),
    releaseDate: checklist?.due_date || '',
    template: {
      label: CHECKLIST_TEMPLATE_LABEL_MAPPING[defaultTemplate],
      value: defaultTemplate,
    },
    uaTeam: getUserInitialValues(checklist?.assignation_mapping['ua'] || []),
  }

  const handleChangeSchema = (selectedOption: IOption) => {
    setValidationSchema(checklistValidation(selectedOption.value as CHECKLIST_TEMPLATE))
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values }) => (
        <Form>
          <ModalBody>
            <RequiredInformation />
            <SelectInput
              {...reactSelectMenuPositionDef(MODAL_ID)}
              label="Template"
              name="template"
              onChange={(value) => handleChangeSchema(value as unknown as IOption)}
              options={Object.values(CHECKLIST_TEMPLATE).map((value) => ({
                label: CHECKLIST_TEMPLATE_LABEL_MAPPING[value],
                value: value,
              }))}
              placeholder="Select the template"
              required
            />
            {values.template.value === CHECKLIST_TEMPLATE.VERSION_RELEASE ? (
              <>
                <SelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  disabled={!!checklist || !!version}
                  focus
                  label="Related version"
                  name="relatedVersion"
                  options={relatedVersionOptions}
                  placeholder="Select the related version"
                  required
                />
                <DateInput
                  label="Release date"
                  name="releaseDate"
                  placeholder="Select a release date"
                  placement="bottom"
                  required
                />
                <TextArea
                  className="changelog-input"
                  label="Changelog"
                  name="changelog"
                  placeholder="Type a changelog"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Product Managers team"
                  name="productManagerTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Product Monetization Squad team"
                  name="productMonetizationSquadTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Developers team"
                  name="developersTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
              </>
            ) : null}
            {values.template.value === CHECKLIST_TEMPLATE.NEW_PUBLISHING_APP ? (
              <>
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Product Managers team"
                  name="productManagerTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="UA team"
                  name="uaTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Mobile team"
                  name="developersTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Backend team"
                  name="backendTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Business Development Team"
                  name="businessDevelopmentTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
              </>
            ) : null}
            {values.template.value === CHECKLIST_TEMPLATE.NEW_MWM_APP ? (
              <>
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Admin team"
                  name="adminTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Backend team"
                  name="backendTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Data team"
                  name="dataTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Developers team"
                  name="developersTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Marketing team"
                  name="marketingTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Monetization team"
                  name="monetizationTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="Product owners team"
                  name="productOwnerTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  label="UA team"
                  name="uaTeam"
                  options={userOptions}
                  placeholder="Select users"
                  required
                />
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button ghost onClick={onClose} type="button">
              Cancel
            </Button>
            <Button disabled={isSubmitting} type="submit">
              Save
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ChecklistModal = ({
  appId,
  checklist,
  isOpen,
  onClose,
  onFailure,
  onSuccess,
  version,
}: ModalProps): ReactElement => {
  return (
    <Modal className="create-checklist-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{!!checklist ? 'Edit to-do list' : 'Create a to-do list'}</ModalHeader>
      <ChecklistForm
        appId={appId}
        checklist={checklist}
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        version={version}
      />
    </Modal>
  )
}

export default ChecklistModal
